import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Activity, AlertTriangle, ShieldCheck, CalendarClock, Pencil, X, Stethoscope, Plus, Trash2 } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Status visual mapping. Tone matches StatusPill tones used elsewhere.
const STATUS_META = {
  healthy:      { label: "Healthy",      tone: "emerald", icon: ShieldCheck, ring: "ring-emerald-300/30", bg: "bg-emerald-300/10",  text: "text-emerald-300" },
  limited:      { label: "Limited",      tone: "cyan",    icon: Activity,    ring: "ring-cyan-300/30",    bg: "bg-cyan-300/10",     text: "text-cyan-300" },
  questionable: { label: "Questionable", tone: "amber",   icon: AlertTriangle, ring: "ring-amber-300/30", bg: "bg-amber-300/10",   text: "text-amber-300" },
  out:          { label: "Out",          tone: "rose",    icon: AlertTriangle, ring: "ring-rose-400/40",  bg: "bg-rose-400/10",     text: "text-rose-300" },
  ir:           { label: "IR",           tone: "rose",    icon: AlertTriangle, ring: "ring-rose-400/50",  bg: "bg-rose-400/15",     text: "text-rose-200" },
};

const STATUS_ORDER = ["healthy", "limited", "questionable", "out", "ir"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function HealthSnapshotWidget({ player, isAdmin = false, onUpdated, compact = false, testId = "health-snapshot-widget" }) {
  const [editOpen, setEditOpen] = useState(false);
  const snap = player?.health_snapshot || null;
  const status = snap?.status || "healthy";
  const meta = STATUS_META[status] || STATUS_META.healthy;
  const Icon = meta.icon;
  const availability = typeof snap?.availability_pct === "number" ? snap.availability_pct : (status === "healthy" ? 100 : null);

  return (
    <GlassCard className={cn("p-5", compact && "p-4")} testId={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold text-base">Health Snapshot</h3>
        </div>
        <div className="flex items-center gap-2">
          {snap?.updated_at && (
            <span className="text-3 text-[11px] font-mono" data-testid={`${testId}-updated`}>
              {fmtRelative(snap.updated_at)}
            </span>
          )}
          {isAdmin && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditOpen(true)}
              data-testid={`${testId}-edit`}
              className="h-7 text-2 hover:text-1"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className={cn("h-12 w-12 rounded-xl ring-1 grid place-items-center", meta.bg, meta.ring)}
        >
          <Icon className={cn("h-5 w-5", meta.text)} />
        </motion.div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className={cn("text-1 text-xl font-semibold")} data-testid={`${testId}-status`}>
              {meta.label}
            </div>
            <StatusPill tone={meta.tone}>{status.toUpperCase()}</StatusPill>
          </div>
          {snap?.current_flag && (
            <div className="text-2 text-sm mt-0.5 truncate" data-testid={`${testId}-flag`}>
              {snap.current_flag}
            </div>
          )}
          {!snap?.current_flag && status === "healthy" && (
            <div className="text-3 text-sm mt-0.5 italic">No flags on file.</div>
          )}
        </div>
      </div>

      {availability != null && (
        <div className="mt-4" data-testid={`${testId}-availability`}>
          <div className="flex items-center justify-between text-3 text-[11px] uppercase tracking-wider">
            <span>Availability</span>
            <span className="text-1 font-mono">{availability}%</span>
          </div>
          <div className="mt-1.5 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${availability}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className={cn(
                "h-full",
                status === "healthy" ? "bg-gradient-to-r from-emerald-300 to-emerald-400" :
                status === "limited" ? "bg-gradient-to-r from-cyan-300 to-blue-400" :
                status === "questionable" ? "bg-gradient-to-r from-amber-300 to-amber-400" :
                "bg-gradient-to-r from-rose-400 to-rose-500"
              )}
            />
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="glass p-3" data-testid={`${testId}-last-practice`}>
          <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <CalendarClock className="h-3 w-3" /> Last Practice
          </div>
          <div className="text-1 text-sm font-medium mt-1">{fmtDate(snap?.last_practice_date)}</div>
        </div>
        <div className="glass p-3" data-testid={`${testId}-next-checkup`}>
          <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <CalendarClock className="h-3 w-3" /> Next Check-Up
          </div>
          <div className="text-1 text-sm font-medium mt-1">{fmtDate(snap?.next_checkup_date)}</div>
        </div>
      </div>

      {snap?.notes_md && (
        <div className="mt-3 glass p-3" data-testid={`${testId}-notes`}>
          <div className="text-3 text-[10px] uppercase tracking-wider mb-1">Notes</div>
          <div className="text-2 text-sm whitespace-pre-wrap leading-relaxed">{snap.notes_md}</div>
        </div>
      )}

      {snap?.injury_history?.length > 0 && (
        <div className="mt-3" data-testid={`${testId}-injury-history`}>
          <div className="text-3 text-[10px] uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Stethoscope className="h-3 w-3" /> Previous Injuries
          </div>
          <div className="space-y-1.5">
            {snap.injury_history.map((inj, i) => (
              <div key={inj.id || i} className="glass p-2.5" data-testid={`${testId}-injury-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-1 text-sm font-medium">{inj.label}</span>
                  <StatusPill tone={inj.recovered ? "emerald" : "amber"}>
                    {inj.recovered ? "Recovered" : "Ongoing"}
                  </StatusPill>
                  {inj.date && <span className="text-3 text-[11px] font-mono">{fmtDate(inj.date)}</span>}
                </div>
                {inj.notes && <div className="text-2 text-xs mt-1 whitespace-pre-wrap leading-relaxed">{inj.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!snap && !isAdmin && (
        <div className="mt-3 text-3 text-xs italic">
          No health update on file. Your advisor will keep this current.
        </div>
      )}

      {isAdmin && (
        <HealthEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          player={player}
          onSaved={onUpdated}
          testIdPrefix={testId}
        />
      )}
    </GlassCard>
  );
}

function HealthEditDialog({ open, onOpenChange, player, onSaved, testIdPrefix }) {
  const initial = player?.health_snapshot || {};
  const [form, setForm] = useState({
    status: initial.status || "healthy",
    availability_pct: initial.availability_pct ?? "",
    current_flag: initial.current_flag || "",
    last_practice_date: initial.last_practice_date || "",
    next_checkup_date: initial.next_checkup_date || "",
    notes_md: initial.notes_md || "",
    injury_history: (initial.injury_history || []).map((i) => ({ ...i })),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const addInjury = () =>
    setForm((s) => ({
      ...s,
      injury_history: [...s.injury_history, { label: "", date: "", recovered: true, notes: "" }],
    }));
  const setInjury = (idx, patch) =>
    setForm((s) => ({
      ...s,
      injury_history: s.injury_history.map((inj, i) => (i === idx ? { ...inj, ...patch } : inj)),
    }));
  const removeInjury = (idx) =>
    setForm((s) => ({ ...s, injury_history: s.injury_history.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        health_snapshot: {
          status: form.status,
          availability_pct: form.availability_pct === "" ? null : Math.max(0, Math.min(100, parseInt(form.availability_pct, 10) || 0)),
          current_flag: form.current_flag.trim() || null,
          last_practice_date: form.last_practice_date || null,
          next_checkup_date: form.next_checkup_date || null,
          notes_md: form.notes_md.trim() || null,
          injury_history: form.injury_history
            .filter((inj) => (inj.label || "").trim())
            .map((inj) => ({
              ...(inj.id ? { id: inj.id } : {}),
              label: inj.label.trim(),
              date: inj.date || null,
              recovered: !!inj.recovered,
              notes: (inj.notes || "").trim() || null,
            })),
          updated_at: new Date().toISOString(),
        },
      };
      const r = await api.patch(`/players/${player.id}`, payload);
      onSaved?.(r.data);
      toast.success("Health snapshot updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirm("Reset current status to healthy? (Previous injuries are kept.)")) return;
    setSaving(true);
    try {
      const r = await api.patch(`/players/${player.id}`, {
        health_snapshot: {
          status: "healthy",
          availability_pct: 100,
          current_flag: null,
          last_practice_date: null,
          next_checkup_date: null,
          notes_md: null,
          injury_history: (form.injury_history || [])
            .filter((inj) => (inj.label || "").trim())
            .map((inj) => ({
              ...(inj.id ? { id: inj.id } : {}),
              label: inj.label.trim(),
              date: inj.date || null,
              recovered: !!inj.recovered,
              notes: (inj.notes || "").trim() || null,
            })),
          updated_at: new Date().toISOString(),
        },
      });
      onSaved?.(r.data);
      toast.success("Health snapshot reset to healthy");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid={`${testIdPrefix}-edit-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-1 flex items-center gap-2">
            <Heart className="h-4 w-4 text-cyan-300" /> Edit Health Snapshot
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="space-y-1">
            <Label className="text-2 text-xs">Status</Label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_ORDER.map((s) => {
                const meta = STATUS_META[s];
                const SIcon = meta.icon;
                const selected = form.status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("status", s)}
                    data-testid={`${testIdPrefix}-status-${s}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ring-1 transition",
                      selected ? cn(meta.bg, meta.ring, meta.text) : "bg-white/5 ring-white/10 text-3 hover:text-1"
                    )}
                  >
                    <SIcon className="h-3.5 w-3.5" /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Availability % (0-100)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.availability_pct}
                onChange={(e) => set("availability_pct", e.target.value)}
                data-testid={`${testIdPrefix}-availability-input`}
                className="bg-white/5 border-white/10 text-1"
                placeholder="e.g. 80"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Current Flag</Label>
              <Input
                value={form.current_flag}
                onChange={(e) => set("current_flag", e.target.value)}
                data-testid={`${testIdPrefix}-flag-input`}
                placeholder="e.g. Right ankle sprain"
                className="bg-white/5 border-white/10 text-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Last Practice</Label>
              <Input
                type="date"
                value={form.last_practice_date ? form.last_practice_date.slice(0, 10) : ""}
                onChange={(e) => set("last_practice_date", e.target.value)}
                data-testid={`${testIdPrefix}-last-practice-input`}
                className="bg-white/5 border-white/10 text-1"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Next Check-Up</Label>
              <Input
                type="date"
                value={form.next_checkup_date ? form.next_checkup_date.slice(0, 10) : ""}
                onChange={(e) => set("next_checkup_date", e.target.value)}
                data-testid={`${testIdPrefix}-next-checkup-input`}
                className="bg-white/5 border-white/10 text-1"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-2 text-xs">Notes (markdown ok)</Label>
            <Textarea
              rows={3}
              value={form.notes_md}
              onChange={(e) => set("notes_md", e.target.value)}
              data-testid={`${testIdPrefix}-notes-input`}
              placeholder="Short medical / availability note for the family…"
              className="bg-white/5 border-white/10 text-1"
            />
          </div>

          <div className="space-y-2 pt-1 border-t border-white/10">
            <div className="flex items-center justify-between">
              <Label className="text-2 text-xs flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5 text-cyan-300" /> Previous Injuries
              </Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={addInjury}
                data-testid={`${testIdPrefix}-injury-add`}
                className="h-7 text-cyan-300 hover:text-cyan-200"
              >
                <Plus className="h-3.5 w-3.5" /> Add injury
              </Button>
            </div>
            {form.injury_history.length === 0 ? (
              <div className="text-3 text-xs italic">No previous injuries logged.</div>
            ) : (
              <div className="space-y-2.5 max-h-[18rem] overflow-y-auto pr-1 scrollbar-thin">
                {form.injury_history.map((inj, idx) => (
                  <div key={idx} className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 space-y-2" data-testid={`${testIdPrefix}-injury-row-${idx}`}>
                    <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                      <div className="space-y-1">
                        <Label className="text-2 text-[10px] uppercase tracking-wide">Injury</Label>
                        <Input
                          value={inj.label}
                          onChange={(e) => setInjury(idx, { label: e.target.value })}
                          placeholder="e.g. Left hamstring strain"
                          className="bg-white/5 border-white/10 text-1 text-sm h-8"
                          data-testid={`${testIdPrefix}-injury-label-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeInjury(idx)}
                        className="text-rose-300/70 hover:text-rose-300 p-1.5 mb-1"
                        data-testid={`${testIdPrefix}-injury-remove-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div className="space-y-1">
                        <Label className="text-2 text-[10px] uppercase tracking-wide">Date</Label>
                        <Input
                          type="date"
                          value={inj.date ? inj.date.slice(0, 10) : ""}
                          onChange={(e) => setInjury(idx, { date: e.target.value })}
                          className="bg-white/5 border-white/10 text-1 text-sm h-8"
                          data-testid={`${testIdPrefix}-injury-date-${idx}`}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-2 text-sm cursor-pointer h-8 px-1">
                        <input
                          type="checkbox"
                          checked={!!inj.recovered}
                          onChange={(e) => setInjury(idx, { recovered: e.target.checked })}
                          className="accent-emerald-400"
                          data-testid={`${testIdPrefix}-injury-recovered-${idx}`}
                        />
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Fully recovered
                      </label>
                    </div>
                    <Input
                      value={inj.notes || ""}
                      onChange={(e) => setInjury(idx, { notes: e.target.value })}
                      placeholder="Optional note (treatment, status, games missed…)"
                      className="bg-white/5 border-white/10 text-1 text-sm h-8"
                      data-testid={`${testIdPrefix}-injury-notes-${idx}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={clear}
            disabled={saving}
            data-testid={`${testIdPrefix}-clear`}
            className="text-rose-300/80 hover:text-rose-300"
          >
            <X className="h-3.5 w-3.5" /> Reset
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
            <Button data-testid={`${testIdPrefix}-save`} disabled={saving} onClick={save} className="cta-primary">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
